import './Area.scss';
import React from "react";
import alertsIcon from '../../../assets/icons/bell-icon.png';
import optionsIcon from '../../../assets/icons/options-icon.png';
import { useAlertsContext } from '../../../contexts/AlertsContext';
import { usePanelContext } from '../../../contexts/PanelContext';
import * as turf from '@turf/turf';
import { useUserContext } from '../../../contexts/UserContext';
import moment from 'moment-timezone';
import mapboxgl from 'mapbox-gl';
import areasIcon from '../../../assets/icons/areas-stroke.png';
import { BellSimpleRingingIcon } from '@phosphor-icons/react';

export default function MyArea(props) {
    const {
        is3D,
        setIs3D
    } = useUserContext();

    const {
        setPanel
    } = usePanelContext();

    const {
        alerts
    } = useAlertsContext();

    const { 
        a,
        map,
        setModal
    } = props;

    const convertTo12HourFormat = (dateTimeString) => {
        const time = moment.tz(dateTimeString, "UTC");
      
        const localTime = time.tz(moment.tz.guess()).format('YYYY-MM-DD h:mm A');
      
        return localTime;
    }

    const formatPolicyAndUniqueIDs = (input) => {
        if (input.includes("Policy ID") && input.includes("Unique ID")) {
          return input.split(',')
                      .map(part => part.trim())
                      .join('<br />');  // Use <br /> tags for HTML line breaks
        }
        return input;
    };

    const WKTtoRings = (wkt) => {
        const rings = wkt.slice(9).slice(0, -2).split(',').map((coordinates) => {
            return coordinates.split(' ').filter((element) => element !== '').map((element) => parseFloat(element));
        });
        return rings;
    };

    const convertMetersToSquareMiles = (wkt) => {
        const data = {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: [WKTtoRings(wkt)]
                }
              }
            ]
        };

        const areaInMetersSquared = turf.area(data);
          
        const conversionFactor = 0.000000386102;
        const areaInSquareMiles = areaInMetersSquared * conversionFactor;
        return (Math.round(areaInSquareMiles * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const viewArea = () => {
        const layers = map.current.getStyle().layers.filter((layer) => layer.id.includes(`Layer-areas-focused`));

        if (layers.length > 0) {
            layers.forEach((layer) => map.current.removeLayer(layer.id))
        }

        if (map.current.getStyle().sources[`Source-areas-focused-${a.id}`]) {
            map.current.removeSource(`Source-areas-focused-${a.id}`);
        }

        const location = [a.longitude, a.latitude];

        const existingPopups = document.getElementsByClassName('popup');
        Array.from(existingPopups).forEach((popup) => popup.remove());

        new mapboxgl.Popup({ offset: 25, maxWidth: '600px', className: 'popup' })
        .setLngLat(location)
        .setHTML(`
          <div>
            <div class='alert-popup-title'>${a.name}</div>
            <div class='alert-popup-row'>
              <div class='alert-popup-row-name'>Description:</div>
              <div class='alert-popup-row-value'>${a.description ? formatPolicyAndUniqueIDs(a.description) : 'No Description'}</div>
            </div>
            <div class='alert-popup-row'>
              <div class='alert-popup-row-name'>Type:</div>
              <div class='alert-popup-row-value'>${a.type === 'point' ? 'POINT/ADDRESS' : 'POLYGON'}</div>
            </div>
            ${a.type === 'polygon'
              ? `<div class='alert-popup-row'>
                  <div class='alert-popup-row-name'>Area:</div>
                  <div class='alert-popup-row-value'>
                    ${convertMetersToSquareMiles(a.geometry)} square miles
                  </div>
                </div>`
              : `<div class='alert-popup-row'>
                  <div class='alert-popup-row-name'>Radius:</div>
                  <div class='alert-popup-row-value'>
                    ${a.radius_miles} miles
                  </div>
                </div>`
            }
            ${a.value ?
               `<div class='alert-popup-row'>
                  <div class='alert-popup-row-name'>Total Asset Value:</div>
                  <div class='alert-popup-row-value'>
                    $${Number(a.value).toLocaleString()}
                  </div>
                </div>` : ' '}
            <div class='alert-popup-row'>
              <div class='alert-popup-row-name'>Created</div>
              <div class='alert-popup-row-value'>${convertTo12HourFormat(a.created_timestamp)}</div>
            </div>
          </div>
        `)
        .addTo(map.current);

        if (a.type === 'point') {
            const generateCircleCoordinates = (center, radius, points = 64) => {
                const [centerLng, centerLat] = center;
                const coordinates = [];
              
                for (let i = 0; i < points; i++) {
                    const angle = (i * 360) / points;
                    const angleRad = (angle * Math.PI) / 180;
              
                    const latitude = centerLat + (radius / 69) * Math.cos(angleRad);
                    const longitude =
                        centerLng +
                        (radius / (69 * Math.cos((centerLat * Math.PI) / 180))) *
                            Math.sin(angleRad);
              
                    coordinates.push([longitude, latitude]);
                }
                coordinates.push(coordinates[0]);
                return coordinates;
              };
          
            const circleCoordinates = generateCircleCoordinates([a.longitude, a.latitude], a.radius_miles);

            const geometry = {
                type: 'Feature',
                properties: a,
                geometry: {
                    type: 'Polygon',
                    coordinates: [circleCoordinates],
                }
            }
            
            map.current.addSource(`Source-areas-focused-${a.id}`, {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [geometry],
                },
            });
    
            map.current.addLayer({
                'id': `Layer-areas-focused-${a.id}-fill`,
                'type': 'fill',
                'source': `Source-areas-focused-${a.id}`,
                'layout': {},
                'paint': {
                  'fill-color': 'rgba(240, 240, 240, 0.4)',
                }
            });
  
            map.current.addLayer({
                'id': `Layer-areas-focused-${a.id}-outline`,
                'type': 'line',
                'source': `Source-areas-focused-${a.id}`,
                'layout': {},
                'paint': {
                  'line-color': 'rgba(255, 255, 255, 1)',
                  'line-width': 2
                }
            });

            map.current.flyTo({ center: [a.longitude, a.latitude], pitch: 0, zoom: 11 });
        } else {
            const rings = WKTtoRings(a.geometry);

            const geometry = {
                'type': 'Feature',
                'geometry': {
                'type': 'Polygon',
                'coordinates': [rings]
                }
            }
        
            map.current.addSource(`Source-areas-focused-${a.id}`, {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [geometry],
                },
            });
        
            map.current.addLayer({
                'id': `Layer-areas-focused-${a.id}-fill`,
                'type': 'fill',
                'source': `Source-areas-focused-${a.id}`,
                'layout': {},
                'paint': {
                  'fill-color': 'rgba(240, 240, 240, 0.4)',
                }
            });
  
            map.current.addLayer({
                'id': `Layer-areas-focused-${a.id}-outline`,
                'type': 'line',
                'source': `Source-areas-focused-${a.id}`,
                'layout': {},
                'paint': {
                  'line-color': 'rgba(255, 255, 255, 1)',
                  'line-width': 2
                }
            });

            map.current.fitBounds(turf.bbox(geometry), {
                padding: 100,
                pitch: 0
            });
        }

        setPanel({
            name: 'Empty',
            content: null
        })

        if (is3D) {
            setIs3D(false);
        }
    
    }

    return (
        <div className='my-area'>
            <img 
                src={optionsIcon}
                className='my-area-options'
                onClick={() => {
                    setModal({
                        type: 'edit-and-delete',
                        content: a
                    })
                }}
            />
            <div className='my-area-name'>
                <img src={areasIcon} alt="Area" className='my-area-name-icon' />
                {a.name}
            </div>
            <div className='my-area-description'>
                <div className='my-area-description-text'>{a.description ? a.description : 'No Description'}</div>
                <div className='my-area-description-type'>Type: {a.type === 'point' ? 'POINT/ADDRESS' : 'POLYGON'}</div>
            </div>
            <div className='my-area-details'>
                {a.geometry ?
                    <div className='my-area-details-area'>
                        <div className='my-area-details-area-title'>Area</div>
                        <div className='my-area-details-area-value'>{convertMetersToSquareMiles(a.geometry)} square miles</div>
                    </div>
                    :
                    <div className='my-area-details-area'> 
                        <div className='my-area-details-area-title'>Radius</div>
                        <div className='my-area-details-area-value'>{a.radius_miles} miles</div>
                    </div>}
                <div className='my-area-details-lastupdated'>
                    <div className='my-area-details-lastupdated-title'>Created</div>
                    <div className='my-area-details-lastupdated-value'>{convertTo12HourFormat(a.created_timestamp)}</div>
                </div>
            </div>
            <div className='my-area-btns'>
               <div className='my-area-btns-btn-switch' onClick={viewArea}>View</div> 
               {(() => {
                   const areaAlerts = alerts.filter((x) => x && x.area_id === a.id);
                   console.log(`Area ${a.id} alerts:`, areaAlerts);
                   console.log('All alerts:', alerts);
                   return areaAlerts.length > 0;
               })() ?
                <div 
                    className='my-area-btns-btn-alerts-on'
                    onClick={() => {
                        setModal({
                            type: 'alerts',
                            content: {
                                setModal: setModal,
                                areaId: a.id
                            }
                        })
                    }}
                    >
                    <BellSimpleRingingIcon size={16} weight='fill'/>
                    Alerts On
                    </div>
                    :
                    <div 
                    className='my-area-btns-btn-alerts-off'
                    onClick={() => {
                    setModal({
                        type: 'alerts',
                        content: {
                            setModal: setModal,
                            areaId: a.id
                        }
                    })
                    }}
                >
                    <BellSimpleRingingIcon size={16} weight='fill'/>
                    Turn On Alerts
                </div>}
            </div>
        </div>
    );
}